import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { SettingsRow, S_INPUT } from "@/components/SettingsRow";
import { Toggle } from "@/components/Toggle";
import { ApiError } from "@/api/client";
import {
  MailConfig,
  getMailConfig,
  putMailConfig,
  testMailConfig,
  testImapConfig,
} from "@/api/settings";

const MODUS_LABEL: Record<string, string> = {
  eml_download: "Mail-Entwurf herunterladen (.eml)",
  smtp_preview: "Über Tool versenden (mit Vorschau)",
};

const TRANSPORT_LABEL: Record<string, string> = {
  smtp: "SMTP",
  microsoft_graph: "Microsoft Graph (OAuth2)",
};

const EMPTY: MailConfig = {
  versand_modus: "eml_download",
  transport: "smtp",
  smtp_host: "",
  smtp_port: "587",
  smtp_user: "",
  smtp_password: "",
  smtp_from: "",
  graph_tenant_id: "",
  graph_client_id: "",
  graph_client_secret: "",
  graph_mailbox: "",
  imap_host: "",
  imap_port: "993",
  imap_user: "",
  imap_password: "",
  imap_use_ssl: "1",
  posteingang_aktiv: "0",
};

export function MailSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState<MailConfig>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [imapTestResult, setImapTestResult] = useState<string | null>(null);

  const q = useQuery({ queryKey: ["me-mail-settings"], queryFn: getMailConfig });

  useEffect(() => {
    if (q.data) {
      setForm({ ...EMPTY, ...q.data });
      setDirty(false);
    }
  }, [q.data]);

  function set(key: keyof MailConfig, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    setMsg(null);
  }

  const saveMut = useMutation({
    mutationFn: () => putMailConfig(form),
    onSuccess: async (data) => {
      setForm({ ...EMPTY, ...data });
      setDirty(false);
      setMsg("Einstellungen gespeichert.");
      await qc.invalidateQueries({ queryKey: ["me-mail-settings"] });
    },
    onError: (e: unknown) => {
      setMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    },
  });

  const testMut = useMutation({
    mutationFn: testMailConfig,
    onSuccess: (data) => setTestResult(data.detail),
    onError: (e: unknown) => {
      setTestResult(e instanceof ApiError ? e.message : "Verbindungstest fehlgeschlagen.");
    },
  });

  const imapTestMut = useMutation({
    mutationFn: testImapConfig,
    onSuccess: (data) => setImapTestResult(data.detail),
    onError: (e: unknown) => {
      setImapTestResult(e instanceof ApiError ? e.message : "IMAP-Verbindungstest fehlgeschlagen.");
    },
  });

  const modus = form.versand_modus || "eml_download";
  const transport = form.transport || "smtp";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ fontSize: 13, color: "var(--fg-muted)", padding: "4px 2px" }}>
        Diese Mail-Einstellungen gelten nur für Ihren Account.
      </div>

      {/* Versandmodus */}
      <Card>
        <SectionHeader
          title="Versandmodus"
          desc="Gilt für Buchungsbestätigung, Infos an Gruppe, Rechnung und Kurtaxe-Anforderung."
          actions={
            <Btn
              variant="primary"
              icon="check"
              size="sm"
              onClick={() => saveMut.mutate()}
              disabled={!dirty || saveMut.isPending}
            >
              Speichern
            </Btn>
          }
        />
        <SettingsRow
          label="Versandmethode"
          hint="Bestimmt, wie Mails für alle Dokumenttypen erzeugt werden."
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(["eml_download", "smtp_preview"] as const).map((m) => (
              <label
                key={m}
                style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13 }}
              >
                <input
                  type="radio"
                  name="versandmodus"
                  value={m}
                  checked={modus === m}
                  onChange={() => set("versand_modus", m)}
                />
                <span>
                  <strong>{MODUS_LABEL[m]}</strong>
                  {m === "eml_download" && (
                    <span style={{ color: "var(--fg-muted)", marginLeft: 6 }}>
                      — .eml-Datei downloaden, per Doppelklick im Mailprogramm öffnen
                    </span>
                  )}
                  {m === "smtp_preview" && (
                    <span style={{ color: "var(--fg-muted)", marginLeft: 6 }}>
                      — Vorschau + Bearbeitung direkt im Browser, Versand per SMTP/Graph
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </SettingsRow>
        {msg && (
          <div style={{ fontSize: 12, color: "var(--fg-muted)", paddingTop: 8 }}>{msg}</div>
        )}
      </Card>

      {/* SMTP / Graph */}
      <Card>
        <SectionHeader
          title="Mail-Server"
          desc="Verbindungseinstellungen für den direkten Versand."
          actions={
            <div style={{ display: "flex", gap: 8 }}>
              <Btn
                variant="ghost"
                icon="electrical_services"
                size="sm"
                onClick={() => {
                  setTestResult(null);
                  testMut.mutate();
                }}
                disabled={testMut.isPending}
              >
                Verbindung testen
              </Btn>
              <Btn
                variant="primary"
                icon="check"
                size="sm"
                onClick={() => saveMut.mutate()}
                disabled={!dirty || saveMut.isPending}
              >
                Speichern
              </Btn>
            </div>
          }
        />

        <SettingsRow label="Versandprotokoll">
          <select
            style={{ ...S_INPUT, maxWidth: 240 }}
            value={transport}
            onChange={(e) => set("transport", e.target.value)}
          >
            {Object.entries(TRANSPORT_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </SettingsRow>

        {transport === "smtp" && (
          <>
            <SettingsRow label="SMTP-Server (Host)">
              <input
                style={S_INPUT}
                value={form.smtp_host}
                onChange={(e) => set("smtp_host", e.target.value)}
                placeholder="smtp.beispiel.de"
                autoComplete="off"
              />
            </SettingsRow>
            <SettingsRow label="SMTP-Port">
              <input
                style={{ ...S_INPUT, maxWidth: 120 }}
                value={form.smtp_port}
                onChange={(e) => set("smtp_port", e.target.value)}
                placeholder="587"
                autoComplete="off"
              />
            </SettingsRow>
            <SettingsRow label="SMTP-Benutzername (E-Mail)">
              <input
                style={S_INPUT}
                value={form.smtp_user}
                onChange={(e) => set("smtp_user", e.target.value)}
                placeholder="absender@beispiel.de"
                autoComplete="off"
              />
            </SettingsRow>
            <SettingsRow label="SMTP-Passwort" hint="Wird verschlüsselt gespeichert. Leer lassen = unverändert.">
              <input
                style={S_INPUT}
                type="password"
                value={form.smtp_password}
                onChange={(e) => set("smtp_password", e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </SettingsRow>
            <SettingsRow label="Absender-Adresse (From)" hint="Leer = SMTP-Benutzername wird verwendet.">
              <input
                style={S_INPUT}
                value={form.smtp_from}
                onChange={(e) => set("smtp_from", e.target.value)}
                placeholder="buchung@pfadfinderzentrum.de"
                autoComplete="off"
              />
            </SettingsRow>
          </>
        )}

        {transport === "microsoft_graph" && (
          <>
            <SettingsRow label="Tenant ID">
              <input
                style={S_INPUT}
                value={form.graph_tenant_id}
                onChange={(e) => set("graph_tenant_id", e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                autoComplete="off"
              />
            </SettingsRow>
            <SettingsRow label="Client ID">
              <input
                style={S_INPUT}
                value={form.graph_client_id}
                onChange={(e) => set("graph_client_id", e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                autoComplete="off"
              />
            </SettingsRow>
            <SettingsRow label="Client Secret" hint="Leer lassen = unverändert.">
              <input
                style={S_INPUT}
                type="password"
                value={form.graph_client_secret}
                onChange={(e) => set("graph_client_secret", e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </SettingsRow>
            <SettingsRow label="Postfach (UPN)" hint="E-Mail-Adresse des sendenden Postfachs.">
              <input
                style={S_INPUT}
                value={form.graph_mailbox}
                onChange={(e) => set("graph_mailbox", e.target.value)}
                placeholder="buchung@organisation.de"
                autoComplete="off"
              />
            </SettingsRow>
          </>
        )}

        {testResult && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              padding: "8px 12px",
              borderRadius: 6,
              background: testResult.toLowerCase().includes("erfolg")
                ? "var(--success-bg, #f0fdf4)"
                : "var(--error-bg, #fef2f2)",
              color: testResult.toLowerCase().includes("erfolg")
                ? "var(--success-fg, #166534)"
                : "var(--error-fg, #991b1b)",
            }}
          >
            {testResult}
          </div>
        )}
      </Card>

      {/* Posteingang (IMAP) */}
      <Card>
        <SectionHeader
          title="Posteingang (IMAP)"
          desc="Eigenes Postfach einbinden, um Mails direkt im Tool zu lesen und zu beantworten."
          actions={
            <div style={{ display: "flex", gap: 8 }}>
              <Btn
                variant="ghost"
                icon="electrical_services"
                size="sm"
                onClick={() => {
                  setImapTestResult(null);
                  imapTestMut.mutate();
                }}
                disabled={imapTestMut.isPending}
              >
                Verbindung testen
              </Btn>
              <Btn
                variant="primary"
                icon="check"
                size="sm"
                onClick={() => saveMut.mutate()}
                disabled={!dirty || saveMut.isPending}
              >
                Speichern
              </Btn>
            </div>
          }
        />

        <SettingsRow
          label="Posteingang aktiv"
          hint="Wenn aktiviert, erscheint „Posteingang“ in der Seitenleiste."
        >
          <Toggle
            on={form.posteingang_aktiv === "1"}
            onChange={(next) => set("posteingang_aktiv", next ? "1" : "0")}
          />
        </SettingsRow>

        <SettingsRow label="IMAP-Server (Host)">
          <input
            style={S_INPUT}
            value={form.imap_host}
            onChange={(e) => set("imap_host", e.target.value)}
            placeholder="imap.beispiel.de"
            autoComplete="off"
          />
        </SettingsRow>
        <SettingsRow label="IMAP-Port">
          <input
            style={{ ...S_INPUT, maxWidth: 120 }}
            value={form.imap_port}
            onChange={(e) => set("imap_port", e.target.value)}
            placeholder="993"
            autoComplete="off"
          />
        </SettingsRow>
        <SettingsRow label="SSL/TLS verwenden" hint="Standard-IMAP über SSL nutzt Port 993.">
          <Toggle
            on={form.imap_use_ssl !== "0"}
            onChange={(next) => set("imap_use_ssl", next ? "1" : "0")}
          />
        </SettingsRow>
        <SettingsRow label="IMAP-Benutzername (E-Mail)">
          <input
            style={S_INPUT}
            value={form.imap_user}
            onChange={(e) => set("imap_user", e.target.value)}
            placeholder="postfach@beispiel.de"
            autoComplete="off"
          />
        </SettingsRow>
        <SettingsRow label="IMAP-Passwort" hint="Wird verschlüsselt gespeichert. Leer lassen = unverändert.">
          <input
            style={S_INPUT}
            type="password"
            value={form.imap_password}
            onChange={(e) => set("imap_password", e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
        </SettingsRow>

        {imapTestResult && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              padding: "8px 12px",
              borderRadius: 6,
              background: imapTestResult.toLowerCase().includes("erfolg")
                ? "var(--success-bg, #f0fdf4)"
                : "var(--error-bg, #fef2f2)",
              color: imapTestResult.toLowerCase().includes("erfolg")
                ? "var(--success-fg, #166534)"
                : "var(--error-fg, #991b1b)",
            }}
          >
            {imapTestResult}
          </div>
        )}
      </Card>
    </div>
  );
}
